<template>  
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-4 py-8 backdrop-blur-sm md:px-6">
    <div class="flex max-h-[90vh] w-full max-w-[1400px] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/10">
      <!-- Left Sidebar for Leagues -->
      <div 
        class="flex h-full flex-col overflow-hidden border-r border-slate-200/80 bg-slate-50 transition-all duration-300"
        :class="sidebarOpen ? 'w-1/4 p-4' : 'w-14 p-2'"
      >
        <!-- Sidebar Header -->
        <div class="mb-4 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <button
              @click="sidebarOpen = !sidebarOpen"
              class="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-white hover:text-slate-800 hover:shadow-sm"
              :title="sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'"
            >
              <svg 
                class="h-5 w-5 transition-transform" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                :class="sidebarOpen ? 'rotate-180' : ''"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </button>
            <h3 v-if="sidebarOpen" class="text-sm font-semibold tracking-tight text-slate-800">Leagues</h3>
          </div>
          <span v-if="sidebarOpen" class="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-500 shadow-sm ring-1 ring-slate-200">{{ fixtureStore.leagues.length }}</span>
        </div>

        <!-- Leagues List -->
        <div v-if="sidebarOpen" class="flex h-full min-h-0 flex-col">
          <div class="flex h-full flex-col space-y-1.5 overflow-y-auto pr-1">
            <div
              v-for="league in fixtureStore.leagues"
              :key="league.id"
              class="cursor-pointer rounded-xl border px-3 py-2.5 transition-all"
              @click="selectLeague(league)"
              :class="selectedLeague === league.league_name
                ? 'border-rose-200 bg-rose-50 shadow-sm'
                : 'border-transparent bg-white/70 hover:border-slate-200 hover:bg-white hover:shadow-sm'"
            >
              <div class="flex items-center gap-2.5">
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold"
                  :class="selectedLeague === league.league_name ? 'bg-rose-500 text-white' : 'bg-slate-100 text-slate-600'"
                >
                  {{ league.league_name?.charAt(0) }}
                </span>
                <span class="truncate text-sm font-medium text-slate-800">{{ league.league_name }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Collapsed State - Show selected league only -->
        <div v-else class="flex h-full flex-col">
          <div class="flex flex-col space-y-2">
            <div
              v-if="selectedLeague"
              class="cursor-pointer rounded-xl border border-rose-200 bg-white px-1.5 py-2 shadow-sm"
              @click="sidebarOpen = true"
              title="Click to expand sidebar"
            >
              <div class="text-center">
                <div class="mx-auto mb-1 flex h-7 w-7 items-center justify-center rounded-lg bg-rose-500">
                  <span class="text-xs font-bold text-white">{{ selectedLeague.charAt(0) }}</span>
                </div>
                <span class="block truncate text-[10px] text-slate-500">{{ selectedLeague }}</span>
              </div>
            </div>
            <button
              v-else
              @click="sidebarOpen = true"
              class="rounded-xl border border-slate-200 bg-white p-2 shadow-sm transition-colors hover:bg-slate-50"
              title="Click to expand sidebar"
            >
              <svg class="mx-auto h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="flex min-w-0 flex-col bg-white"
           :class="sidebarOpen ? 'w-3/4' : 'w-[calc(100%-3.5rem)]'">
      <!-- Header -->
      <div class="border-b border-slate-100 px-6 py-5">
        <div class="flex items-start justify-between gap-4">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white shadow-sm">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
            <div>
              <h2 class="text-xl font-semibold tracking-tight text-slate-900">Global Search</h2>
              <p class="mt-0.5 text-sm text-slate-500">Search fixtures or jump to a quick action</p>
            </div>
          </div>
          <button 
            @click="closePopup" 
            class="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>

      <!-- Tabs -->
      <div class="border-b border-slate-100 px-6 py-3">
        <div class="inline-flex rounded-xl bg-slate-100 p-1">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            @click="activeTab = tab.value"
            class="rounded-lg px-3.5 py-1.5 text-sm font-medium transition-all"
            :class="activeTab === tab.value 
              ? 'bg-white text-slate-900 shadow-sm' 
              : 'text-slate-500 hover:text-slate-700'"
          >
            <span class="flex items-center gap-2">
              {{ tab.label }}
              <span 
                v-if="hasCustomFilter && activeTab === tab.value" 
                class="inline-flex items-center rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700"
              >
                Custom
              </span>
            </span>
          </button>
        </div>
      </div>

      <!-- Search and Filters -->
      <SearchFilters
        v-model:searchQuery="searchQuery"
        v-model:selectedLeague="selectedLeague"
        v-model:selectedStatus="selectedStatus"
        v-model:selectedDateRange="selectedDateRange"
        v-model:customRange="customRange"
        :availableLeagues="availableLeagues"
        :filteredCount="getFilteredCount"
        :totalCount="getTotalCount"
        @applyCustomRange="applyCustomRange"
        @clear="clearFilters"
        @runCommand="runCommand"
      />

      <!-- Fixtures/Players List -->
      <div class="flex-1 overflow-hidden">
        <div v-if="showCommandResults" class="border-b border-slate-100 px-6 py-3">
          <QuickActions
            :commands="matchedCommands"
            :selected-index="commandSelectedIndex"
            :show-list="true"
            @run="runCommand"
          />
        </div>

        <LoadingState v-if="loading && !isCommandOnlyQuery" />
        
        <!-- Player Search Results -->
        <template v-if="isPlayerSearch && !isCommandOnlyQuery">
          <EmptyState 
            v-if="allPlayers.length === 0" 
            title="No players found"
            message="Try adjusting your search criteria"
          />
          
          <PlayerSearchResults
            v-else
            ref="fixtureResultsRef"
            :players="allPlayers"
            :selectedIndex="resultSelectedIndex"
            @select="selectPlayer"
          />
        </template>
        
        <!-- Fixture/Outright Search Results -->
        <template v-else-if="!isCommandOnlyQuery">
          <EmptyState 
            v-if="filteredFixtures.length === 0" 
            :title="searchQuery ? 'No fixtures found' : 'No fixtures available'"
            :message="searchQuery ? 'Try adjusting your search criteria' : 'No fixtures are currently available'"
          />
          
          <FixtureSearchResults
            v-else
            ref="fixtureResultsRef"
            :fixtures="filteredFixtures"
            :type="activeTab === 'outrights' ? 'outright' : 'fixture'"
            :selectedIndex="resultSelectedIndex"
            @select="selectFixture"
            @view="viewFixture"
          />
        </template>

        <EmptyState
          v-else-if="isCommandOnlyQuery && matchedCommands.length === 0"
          title="No quick actions found"
          message="Try another command name, or type > to list all actions"
        />
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useProjectionsStore } from '../../stores/projections'
import { useFixtureStore } from '../../stores/fixtureStore'
import axios from '../../axios.js'
import SearchFilters from './SearchFilters.vue'
import FixtureSearchResults from './FixtureSearchResults.vue'
import PlayerSearchResults from './PlayerSearchResults.vue'
import LoadingState from './LoadingState.vue'
import EmptyState from './EmptyState.vue'
import QuickActions from './QuickActions.vue'
import { isCommandQuery, matchQuickCommands, runQuickCommand } from './quickCommands'

const router = useRouter()
const projectionsStore = useProjectionsStore()
const fixtureStore = useFixtureStore()



// Reactive data
const searchQuery = ref('')
const selectedLeague = ref('')
const selectedStatus = ref('')
const selectedDateRange = ref('today')
const customRange = ref({ start: '', end: '' })
const loading = ref(false)
const allFixtures = ref([])
const allOutrights = ref([])
const allPlayers = ref([])
const searchTimeout = ref(null)
const fetchedLeagues = ref([])
const selectedIndex = ref(0)
const fixtureResultsRef = ref(null)
const isPlayerSearch = ref(false)

// Tab state
const activeTab = ref('all')
const hasCustomFilter = ref(false)
const sidebarOpen = ref(false)
const baseTabs = [
  { value: 'all', label: 'All' },
  { value: 'fixtures', label: 'Fixtures' },
  { value: 'outrights', label: 'Outrights' }
]

const tabs = computed(() => {
  if (hasCustomFilter.value) {
    return [
      ...baseTabs,
      { value: 'custom', label: 'Custom' }
    ]
  }
  return baseTabs
})

const isCommandOnlyQuery = computed(() => isCommandQuery(searchQuery.value))
const matchedCommands = computed(() => {
  const query = searchQuery.value.trim()
  if (!query) return []
  if (isPlayerSearch.value && !isCommandOnlyQuery.value) return []
  return matchQuickCommands(query)
})
const showCommandResults = computed(() => matchedCommands.value.length > 0)
const commandCount = computed(() => showCommandResults.value ? matchedCommands.value.length : 0)
const resultCount = computed(() => {
  if (isCommandOnlyQuery.value) return 0
  return isPlayerSearch.value ? allPlayers.value.length : filteredFixtures.value.length
})
const commandSelectedIndex = computed(() => {
  if (!showCommandResults.value) return -1
  return selectedIndex.value < commandCount.value ? selectedIndex.value : -1
})
const resultSelectedIndex = computed(() => {
  if (isCommandOnlyQuery.value) return -1
  return Math.max(-1, selectedIndex.value - commandCount.value)
})

// Computed properties
const availableLeagues = computed(() => {
  const leagues = new Set()
  allFixtures.value.forEach(fixture => {
    if (fixture.league?.name) {
      leagues.add(fixture.league.name)
    }
  })
  return Array.from(leagues).sort()
})

const allLeagues = computed(() => {
  return fetchedLeagues.value
})

// Get content based on active tab
const getCurrentContent = computed(() => {
  switch (activeTab.value) {
    case 'fixtures':
      return allFixtures.value
    case 'outrights':
      return allOutrights.value
    case 'custom':
      // For custom tab, return filtered content
      return [...allFixtures.value, ...allOutrights.value]
    case 'all':
    default:
      return [...allFixtures.value, ...allOutrights.value]
  }
})

const filteredFixtures = computed(() => {
  let result = [...getCurrentContent.value]

  // Only apply filters when on custom tab or when specific filters are active (but not for search queries)
  const shouldApplyFilters = activeTab.value === 'custom' || (hasCustomFilter.value && !searchQuery.value)

  if (shouldApplyFilters) {
    // Apply league filter
    if (selectedLeague.value) {
      result = result.filter(item => {
        if (activeTab.value === 'outrights') {
          return item.league?.name === selectedLeague.value
        }
        return item.league?.league_name === selectedLeague.value
      })
    }

    // Apply status filter (if we have status data)
    if (selectedStatus.value) {
      result = result.filter(item => item.status === selectedStatus.value)
    }

    // Apply date range filter (only for fixtures)
    if (selectedDateRange.value && activeTab.value !== 'outrights') {
      result = result.filter(item => {
        if (!item.gamedate) return false
        
        const itemDate = new Date(item.gamedate)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
      
      switch (selectedDateRange.value) {
        case 'today':
          const todayEnd = new Date(today)
          todayEnd.setDate(todayEnd.getDate() + 1)
          return itemDate >= today && itemDate < todayEnd
          
        case 'tomorrow':
          const tomorrow = new Date(today)
          tomorrow.setDate(tomorrow.getDate() + 1)
          const tomorrowEnd = new Date(tomorrow)
          tomorrowEnd.setDate(tomorrowEnd.getDate() + 1)
          return itemDate >= tomorrow && itemDate < tomorrowEnd
          
        case 'next3':
          const next3Days = new Date(today)
          next3Days.setDate(next3Days.getDate() + 3)
          return itemDate >= today && itemDate < next3Days
          
        case 'next7':
          const next7Days = new Date(today)
          next7Days.setDate(next7Days.getDate() + 7)
          return itemDate >= today && itemDate < next7Days

        case 'next14':
          const next14Days = new Date(today)
          next14Days.setDate(next14Days.getDate() + 14)
          return itemDate >= today && itemDate < next14Days

        case 'next30':
          const next30Days = new Date(today)
          next30Days.setDate(next30Days.getDate() + 30)
          return itemDate >= today && itemDate < next30Days

        case 'prev7':
          const prev7Days = new Date(today)
          prev7Days.setDate(prev7Days.getDate() - 7)
          return itemDate >= prev7Days && itemDate < today

        case 'prev14':
          const prev14Days = new Date(today)
          prev14Days.setDate(prev14Days.getDate() - 14)
          return itemDate >= prev14Days && itemDate < today

        case 'prev30':
          const prev30Days = new Date(today)
          prev30Days.setDate(prev30Days.getDate() - 30)
          return itemDate >= prev30Days && itemDate < today

        case 'prev90':
          const prev90Days = new Date(today)
          prev90Days.setDate(prev90Days.getDate() - 90)
          return itemDate >= prev90Days && itemDate < today

        case 'custom':
          if (customRange.value.start && customRange.value.end) {
            const startDate = new Date(customRange.value.start + 'T00:00:00')
            const endDate = new Date(customRange.value.end + 'T23:59:59')
            return itemDate >= startDate && itemDate <= endDate
          }
          return true
          
        default:
          return true
      }
    })
  }
  }

  return result
})

// Computed properties for counts
const getFilteredCount = computed(() => {
  return filteredFixtures.value.length
})

const getTotalCount = computed(() => {
  switch (activeTab.value) {
    case 'fixtures':
      return allFixtures.value.length
    case 'outrights':
      return allOutrights.value.length
    case 'all':
    default:
      return allFixtures.value.length + allOutrights.value.length
  }
})

// Methods
const closePopup = () => {
  projectionsStore.globalSearchPopup = false
}

const clearFilters = () => {
  searchQuery.value = ''
  selectedLeague.value = ''
  selectedStatus.value = ''
  selectedDateRange.value = 'today'
  customRange.value = { start: '', end: '' }
  selectedIndex.value = 0
  hasCustomFilter.value = false
  isPlayerSearch.value = false
  allPlayers.value = []
  
  // Switch back to appropriate tab when clearing filters
  if (activeTab.value === 'custom') {
    activeTab.value = 'all'
  }
  
  // Reload latest content when clearing filters based on active tab
  switch (activeTab.value) {
    case 'fixtures':
      loadLatestFixtures()
      break
    case 'outrights':
      loadLatestOutrights()
      break
    case 'all':
    default:
      Promise.all([
        loadLatestFixtures(),
        loadLatestOutrights()
      ])
      break
  }
}

const applyCustomRange = () => {
  // Custom range is already applied through the computed filteredFixtures
  // This method is called when the user clicks "Apply Range" in the DateRangePicker
  console.log('Custom range applied:', customRange.value)
}

const selectFixture = (fixture) => {
  // Navigate to the appropriate route based on active tab
  closePopup()
  const route = activeTab.value === 'outrights' ? `/outrights/${fixture.id}` : `/fixture/${fixture.id}`
  router.push(route)
}

const viewFixture = (fixture) => {
  closePopup()
  const route = activeTab.value === 'outrights' ? `/outrights/${fixture.id}` : `/fixture/${fixture.id}`
  router.push(route)
}

const selectPlayer = (player) => {
  closePopup()
  const playerId = player.player_id || player.id
  router.push(`/player/${playerId}`)
}

const runCommand = (command) => {
  if (!command?.action && !command?.to) return
  closePopup()
  runQuickCommand(command, router)
}

const selectLeague = (league) => {
  selectedLeague.value = league.league_name
  hasCustomFilter.value = true
  // Expand sidebar if it's collapsed when selecting a league
  if (!sidebarOpen.value) {
    sidebarOpen.value = true
  }
}

const loadLatestFixtures = async () => {
  loading.value = true
  try {
    const params = { 
      league: selectedLeague.value,
      status: selectedStatus.value,
      dateRange: selectedDateRange.value || 'today',
      customRange: customRange.value
    }
    
    const response = await axios.get('/api/fixtures/global-search', { params })
    allFixtures.value = response.data // Use direct API response
  } catch (error) {
    console.error('Error loading latest fixtures:', error)
    allFixtures.value = []
  } finally {
    loading.value = false
  }
}

const loadLatestOutrights = async () => {
  loading.value = true
  try {
    const params = { 
      league: selectedLeague.value,
      status: selectedStatus.value
    }
    
    const response = await axios.get('/api/outrights/global-search', { params })
    allOutrights.value = response.data // Use direct API response
  } catch (error) {
    console.error('Error loading latest outrights:', error)
    allOutrights.value = []
  } finally {
    loading.value = false
  }
}

const searchFixtures = async (query) => {
  // Don't reset data if query is empty - just return without doing anything
  if (!query.trim()) {
    return
  }

  loading.value = true
  try {
    const params = { 
      search: query,
      league: selectedLeague.value,
      status: selectedStatus.value,
    }
    // Keep name/id search unbounded on the default Today chip. Explicit ranges still apply.
    if (selectedDateRange.value && selectedDateRange.value !== 'today') {
      params.dateRange = selectedDateRange.value
    }
    if (customRange.value.start && customRange.value.end) {
      params.customRange = customRange.value
    }
    
    const response = await axios.get('/api/fixtures/global-search', { params })
    allFixtures.value = response.data // Use direct API response
  } catch (error) {
    console.error('Error searching fixtures:', error)
    allFixtures.value = []
  } finally {
    loading.value = false
  }
}

const searchOutrights = async (query) => {
  // Don't reset data if query is empty - just return without doing anything
  if (!query.trim()) {
    return
  }

  loading.value = true
  try {
    const params = { 
      search: query,
      league: selectedLeague.value,
      status: selectedStatus.value
    }
    
    const response = await axios.get('/api/outrights/global-search', { params })
    allOutrights.value = response.data // Use direct API response
  } catch (error) {
    console.error('Error searching outrights:', error)
    allOutrights.value = []
  } finally {
    loading.value = false
  }
}

const searchPlayers = async (query) => {
  loading.value = true
  try {
    // Allow empty query - it will return latest players
    const params = { 
      search: query || ''
    }
    
    console.log('Searching players with query:', query, 'params:', params) // Debug log
    
    const response = await axios.get('/api/players/global-search', { params })
    console.log('Player search response:', response.data) // Debug log
    allPlayers.value = response.data // Use direct API response
  } catch (error) {
    console.error('Error searching players:', error)
    allPlayers.value = []
  } finally {
    loading.value = false
  }
}

const formatFixtureData = (fixture) => {
  return {
    id: fixture.id,
    date: fixture.gamedate ? fixture.gamedate.split(' ')[0] : '',
    time: fixture.gamedate ? fixture.gamedate.split(' ')[1] : '',
    homeTeam: fixture.home_team?.team_name || fixture.home_team?.name || fixture.home_team || 'Unknown Team',
    awayTeam: fixture.away_team?.team_name || fixture.away_team?.name || fixture.away_team || 'Unknown Team',
    league: fixture.league?.league_name || fixture.league?.name || fixture.league || 'Unknown League',
    status: fixture.status || 'Scheduled',
    lastUpdated: fixture.updated_at || fixture.created_at
  }
}

const debouncedSearch = (query) => {
  console.log('🚀 debouncedSearch called with query:', query) // Debug log
  
  // Clear existing timeout
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
  }
  
  // Handle empty query - but still check for Player: prefix
  if (!query) {
    query = ''
  }
  
  const trimmedQuery = query.trim()
  if (isCommandQuery(trimmedQuery)) {
    isPlayerSearch.value = false
    return
  }

  // Check if query starts with "Player:" prefix
  const playerPrefix = 'Player:'
  const isPlayerQuery = trimmedQuery.toLowerCase().startsWith(playerPrefix.toLowerCase())
  
  console.log('🎯 Is player query?', isPlayerQuery, 'trimmedQuery:', trimmedQuery) // Debug log
  
  if (isPlayerQuery) {
    // Extract the search term after "Player:"
    const playerSearchTerm = trimmedQuery.substring(playerPrefix.length).trim()
    isPlayerSearch.value = true
    
    console.log('👤 Player search detected, search term:', playerSearchTerm) // Debug log
    
    // Set new timeout for debounced search
    searchTimeout.value = setTimeout(() => {
      // Always call searchPlayers, even if searchTerm is empty (will return latest players)
      console.log('📞 Calling searchPlayers with term:', playerSearchTerm) // Debug log
      searchPlayers(playerSearchTerm)
    }, 300) // 300ms delay
  } else {
    isPlayerSearch.value = false
    
    // Don't search if query is empty and not a player query
    if (!trimmedQuery) {
      console.log('⏸️ Empty query, skipping search')
      return
    }
    
    // Set new timeout for debounced search
    searchTimeout.value = setTimeout(() => {
      console.log('🔎 Searching fixtures/outrights for:', query)
      // Search based on active tab
      switch (activeTab.value) {
        case 'fixtures':
          searchFixtures(query)
          break
        case 'outrights':
          searchOutrights(query)
          break
        case 'all':
        default:
          // For 'all' tab, search both fixtures and outrights
          Promise.all([
            searchFixtures(query),
            searchOutrights(query)
          ])
          break
      }
    }, 300) // 300ms delay
  }
}



// Watch for tab changes
watch(activeTab, () => {
  // Reset selected index when tab changes
  selectedIndex.value = 0
  
  // Load appropriate content when tab changes (but keep search query)
  switch (activeTab.value) {
    case 'fixtures':
      if (searchQuery.value && searchQuery.value.trim()) {
        searchFixtures(searchQuery.value)
      } else {
        loadLatestFixtures()
      }
      break
    case 'outrights':
      if (searchQuery.value && searchQuery.value.trim()) {
        searchOutrights(searchQuery.value)
      } else {
        loadLatestOutrights()
      }
      break
    case 'all':
    default:
      if (searchQuery.value && searchQuery.value.trim()) {
        Promise.all([
          searchFixtures(searchQuery.value),
          searchOutrights(searchQuery.value)
        ])
      } else {
        Promise.all([
          loadLatestFixtures(),
          loadLatestOutrights()
        ])
      }
      break
  }
})

// Watch for filter changes to set custom filter state and trigger search
watch([selectedLeague, selectedStatus, selectedDateRange, customRange], () => {
  const hasNonDefaultDate = !!(selectedDateRange.value && selectedDateRange.value !== 'today')
  const hasFilters = !!(selectedLeague.value || selectedStatus.value || hasNonDefaultDate || (customRange.value.start && customRange.value.end))
  hasCustomFilter.value = hasFilters
  
  // Default Today chip should not force the Custom tab
  if (hasFilters && activeTab.value !== 'custom') {
    activeTab.value = 'custom'
  }
  
  if (searchQuery.value && searchQuery.value.trim()) {
    debouncedSearch(searchQuery.value)
    return
  }

  // Date chips must refetch; empty-query search is a no-op
  switch (activeTab.value) {
    case 'fixtures':
      loadLatestFixtures()
      break
    case 'outrights':
      loadLatestOutrights()
      break
    case 'custom':
    case 'all':
    default:
      Promise.all([
        loadLatestFixtures(),
        loadLatestOutrights()
      ])
      break
  }
})

// Separate watch for search query to handle search functionality properly
watch(searchQuery, (newQuery, oldQuery) => {
  // Don't automatically switch to custom tab for search queries
  // Let the search work based on the current active tab
  
  console.log('🔍 Search query changed:', newQuery, 'old:', oldQuery) // Debug log
  
  // Always call debouncedSearch if there's a query
  if (newQuery && newQuery.trim()) {
    console.log('✅ Calling debouncedSearch with query:', newQuery) // Debug log
    debouncedSearch(newQuery)
  } else {
    // Reset player search mode when query is cleared
    console.log('🔄 Query cleared, resetting player search') // Debug log
    isPlayerSearch.value = false
    allPlayers.value = []
  }
}, { immediate: false })

// Reset selected index when filtered fixtures change
watch(filteredFixtures, () => {
  selectedIndex.value = 0
})

// Reset selected index when players change
watch(allPlayers, () => {
  selectedIndex.value = 0
})

watch(matchedCommands, () => {
  selectedIndex.value = 0
})

const loadAvailableLeagues = async () => {
  const response = await axios.get('/api/leagues/get-leagues')
  fetchedLeagues.value = response.data
}

// Lifecycle
onMounted(async () => {
  // Load leagues first
  await fixtureStore.getLeagues()
  
  // Load both fixtures and outrights on mount
  Promise.all([
    loadLatestFixtures(),
    loadLatestOutrights()
  ])
  
  // Add keyboard event listeners
  document.addEventListener('keydown', handleKeydown)
})

// Watch for popup state changes
watch(() => projectionsStore.globalSearchPopup, async (isOpen) => {
  if (isOpen) {
    // Load leagues if not already loaded
    if (fixtureStore.leagues.length === 0) {
      await fixtureStore.getLeagues()
    }
    
    // Load appropriate content based on active tab
    switch (activeTab.value) {
      case 'fixtures':
        if (searchQuery.value && searchQuery.value.trim()) {
          searchFixtures(searchQuery.value)
        } else {
          loadLatestFixtures()
        }
        break
      case 'outrights':
        if (searchQuery.value && searchQuery.value.trim()) {
          searchOutrights(searchQuery.value)
        } else {
          loadLatestOutrights()
        }
        break
      case 'all':
      default:
        if (searchQuery.value && searchQuery.value.trim()) {
          Promise.all([
            searchFixtures(searchQuery.value),
            searchOutrights(searchQuery.value)
          ])
        } else {
          Promise.all([
            loadLatestFixtures(),
            loadLatestOutrights()
          ])
        }
        break
    }
    selectedIndex.value = 0
  }
})

// Cleanup timeout on unmount
onUnmounted(() => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
  }
  // Remove keyboard event listeners
  document.removeEventListener('keydown', handleKeydown)
})

// Keyboard navigation methods
const handleKeydown = (event) => {
  // Only handle keyboard events when popup is open
  if (!projectionsStore.globalSearchPopup) return
  
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      moveSelection(1)
      break
    case 'ArrowUp':
      event.preventDefault()
      moveSelection(-1)
      break
    case 'Enter':
      event.preventDefault()
      if (event.ctrlKey || event.metaKey) {
        // Ctrl+Enter - open in new tab
        selectCurrentFixtureNewTab()
      } else {
        // Regular Enter - open in current tab
        selectCurrentFixture()
      }
      break
    case 'Escape':
      event.preventDefault()
      closePopup()
      break
  }
}

const moveSelection = (direction) => {
  const maxItems = commandCount.value + resultCount.value
  const maxIndex = maxItems - 1
  if (maxIndex < 0) return
  const newIndex = Math.max(0, Math.min(maxIndex, selectedIndex.value + direction))
  
  if (newIndex !== selectedIndex.value) {
    selectedIndex.value = newIndex
    // Scroll to keep selected item in view
    scrollToSelectedItem()
  }
}

const selectCurrentFixture = () => {
  if (showCommandResults.value && selectedIndex.value < commandCount.value) {
    runCommand(matchedCommands.value[selectedIndex.value])
    return
  }

  const resultIndex = resultSelectedIndex.value
  if (resultIndex < 0) return

  if (isPlayerSearch.value) {
    if (allPlayers.value.length > 0) {
      const selectedPlayer = allPlayers.value[resultIndex]
      closePopup()
      const playerId = selectedPlayer.player_id || selectedPlayer.id
      router.push(`/player/${playerId}`)
    }
  } else if (filteredFixtures.value.length > 0) {
    const selectedFixture = filteredFixtures.value[resultIndex]
    closePopup()
    const route = activeTab.value === 'outrights' ? `/outrights/${selectedFixture.id}` : `/fixture/${selectedFixture.id}`
    router.push(route)
  }
}

const selectCurrentFixtureNewTab = () => {
  if (showCommandResults.value && selectedIndex.value < commandCount.value) {
    const command = matchedCommands.value[selectedIndex.value]
    if (command?.to) {
      window.open(command.to, '_blank', 'noopener,noreferrer')
    } else {
      runCommand(command)
    }
    return
  }

  const resultIndex = resultSelectedIndex.value
  if (resultIndex < 0) return

  if (isPlayerSearch.value) {
    if (allPlayers.value.length > 0) {
      const selectedPlayer = allPlayers.value[resultIndex]
      const playerId = selectedPlayer.player_id || selectedPlayer.id
      window.open(`/player/${playerId}`, '_blank', 'noopener,noreferrer')
    }
  } else if (filteredFixtures.value.length > 0) {
    const selectedFixture = filteredFixtures.value[resultIndex]
    const url = activeTab.value === 'outrights' ? `/outrights/${selectedFixture.id}` : `/fixture/${selectedFixture.id}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}

const scrollToSelectedItem = () => {
  // Give Vue time to update the DOM
  nextTick(() => {
    const container = fixtureResultsRef.value?.$el?.querySelector('.overflow-y-auto')
    if (!container) return
    
    const selectedElement = container.querySelector('[class*="bg-blue-50"]')
    if (!selectedElement) return
    
    const containerRect = container.getBoundingClientRect()
    const elementRect = selectedElement.getBoundingClientRect()
    
    // Check if element is not fully visible
    if (elementRect.top < containerRect.top || elementRect.bottom > containerRect.bottom) {
      selectedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      })
    }
  })
}
</script>